import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, WifiOff } from "lucide-react";
import PhotoCapture from "../components/PhotoCapture";
import BarcodeCapture from "../components/BarcodeCapture";
import DescribeCapture from "../components/DescribeCapture";
import RecipeCapture from "../components/RecipeCapture";
import MealReview from "../components/MealReview";
import DailyDashboard from "../components/DailyDashboard";
import MealSection from "../components/MealSection";
import ShareMealModal from "../components/ShareMealModal";
import WeeklyChart from "../components/WeeklyChart";
import WeeklyInsight from "../components/WeeklyInsight";
import MigrationBanner from "../components/MigrationBanner";
import AddFoodPicker from "../components/AddFoodPicker";
import PromptModal from "../components/PromptModal";
import AnalyzingOverlay from "../components/AnalyzingOverlay";
import { estimateMeal } from "../lib/vision/orchestrator";
import { lookupBarcode } from "../lib/barcode/openfoodfacts";
import { lookupUsdaBarcode } from "../lib/barcode/usda";
import {
  deleteMealRemote,
  loadMealsRemote,
  mealsByType,
  mealsOnDate,
  saveMealRemote,
  todayISO
} from "../lib/storage";
import { loadFavorites, saveFavoriteFromMeal, type Favorite } from "../lib/favorites";
import { loadTodayWater, type WaterLog } from "../lib/water";
import { computeStreak } from "../lib/streaks";
import { friendlyError } from "../lib/errors";
import { formatSyncedAt, loadWithCache } from "../lib/cache";
import { supabase } from "../lib/supabase";
import { DashboardSkeleton } from "../components/Skeletons";
import DateNav from "../components/DateNav";
import type { ProgressPhase } from "../lib/vision/types";
import type { SearchResult } from "../lib/barcode/openfoodfacts";
import { genericFoodToEstimate, type GenericFood } from "../lib/generic-foods";
import type { Suggestion as AiSuggestion } from "../lib/ai";
import { DEFAULT_GOALS, loadGoalsRemote, runAdaptiveMacros, targetKcalForMealType, type AdaptResult, type Goals } from "../lib/goals";
import {
  MEAL_TYPES,
  MEAL_TYPE_LABELS,
  scaleEstimate,
  sumTotals,
  type LoggedMeal,
  type MealEstimate,
  type MealType
} from "../lib/types";

type CaptureMode = "photo" | "barcode" | "describe" | "recipe";

type Stage =
  | { kind: "today" }
  | { kind: "picker"; mealType: MealType }
  | { kind: "capture"; mode: CaptureMode; mealType: MealType }
  | { kind: "analyzing"; mode: CaptureMode; mealType: MealType; imageDataUrl?: string; caption?: string }
  | {
      kind: "review";
      mealType: MealType;
      imageDataUrl?: string;
      externalPhotoUrl?: string;
      caption?: string;
      estimate: MealEstimate;
      disagreement_pct: number;
      sourceProviders: string[];
    }
  | {
      kind: "error";
      message: string;
      mealType: MealType;
      mode: CaptureMode;
      /** Preserved on error so the user can retry without re-capturing. */
      imageDataUrl?: string;
      caption?: string;
      /** Barcode mode passes a scanned code; preserved so retry doesn't re-scan. */
      barcode?: string;
    };

interface Props {
  onReviewing: (reviewing: boolean) => void;
  onStageChange?: (label: string) => void;
}

export default function Capture({ onReviewing, onStageChange }: Props) {
  const navigate = useNavigate();
  const [stage, setStage] = useState<Stage>({ kind: "today" });
  const [meals, setMeals] = useState<LoggedMeal[]>([]);
  const [favorites, setFavorites] = useState<Favorite[]>([]);
  const [goals, setGoals] = useState<Goals>(DEFAULT_GOALS);
  // Adaptive-macros result from the most recent runAdaptiveMacros() call.
  // When applied=true on Today load, we surface a one-time banner so the
  // user knows their target changed (and why).
  const [adaptResult, setAdaptResult] = useState<AdaptResult | null>(null);
  const [water, setWater] = useState<WaterLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [justSavedCalories, setJustSavedCalories] = useState<number | null>(null);
  const [pendingFavorite, setPendingFavorite] = useState<{ meal: LoggedMeal; suggested: string } | null>(null);
  /** When > 0, the data on screen is from localStorage cache and this timestamp shows when it was last live-fetched. */
  const [staleSince, setStaleSince] = useState<number | null>(null);
  /** Progress phase reported by the vision provider during analyzing. */
  const [analyzingPhase, setAnalyzingPhase] = useState<ProgressPhase>("uploading");
  /** Lets us abort an in-flight Gemini call from the Cancel button. */
  const abortRef = useRef<AbortController | null>(null);
  /** Save-button state while the meal is being persisted — separate from
      loadError because that one only renders inside the Today view. */
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  /** Bulk-share state — populated when the user taps "Share breakfast" on
   *  a MealSection. Lives at the Capture level so only one modal exists
   *  at a time and so the same ShareMealModal handles both single and
   *  bulk share via its `meals` array prop. */
  const [shareBatch, setShareBatch] = useState<{ meals: LoggedMeal[]; label: string } | null>(null);
  /** YYYY-MM-DD of the day the dashboard is showing. Defaults to today.
      Saved meals land on this date (noon local time, see saveOptionsForDate). */
  const [selectedDate, setSelectedDate] = useState<string>(todayISO());

  function newAbortController(): AbortController {
    abortRef.current?.abort(); // safety: kill any stale call
    const ac = new AbortController();
    abortRef.current = ac;
    setAnalyzingPhase("uploading");
    return ac;
  }

  function cancelAnalyze() {
    abortRef.current?.abort();
    abortRef.current = null;
    // Drop back to capture so the user can try again or pick a different mode.
    if (stage.kind === "analyzing") {
      setStage({ kind: "capture", mode: stage.mode, mealType: stage.mealType });
    }
  }

  const refresh = useCallback(async () => {
    try {
      // Scope cache per user so a shared device doesn't show one account's
      // meals while another is signed in.
      const userId = supabase ? (await supabase.auth.getUser()).data.user?.id ?? null : null;

      // Run all four loads in parallel, each with its own cache fallback.
      // If a single load fails, the other three still update. If ALL fail,
      // we still get the last-known state instead of a blank red screen.
      const [m, g, f, w] = await Promise.all([
        loadWithCache("meals", userId, loadMealsRemote),
        loadWithCache("goals", userId, loadGoalsRemote),
        loadWithCache("favorites", userId, loadFavorites),
        loadWithCache("water-today", userId, loadTodayWater)
      ]);

      setMeals(m.data);
      setGoals(g.data);
      setFavorites(f.data);
      setWater(w.data);

      // If any load came from cache, surface the offline pill. Use the
      // oldest syncedAt so the timestamp doesn't lie about which slice is stale.
      const anyStale = [m, g, f, w].some((r) => r.fromCache);
      setStaleSince(anyStale ? Math.min(m.syncedAt, g.syncedAt, f.syncedAt, w.syncedAt) : null);
      setLoadError(null);

      // Adaptive macros — runs in the background after the main load. The RPC
      // self-throttles (returns applied=false if it adjusted in the last 6
      // days), so calling this on every Today load is cheap. If it DID
      // adjust, re-fetch goals so the new target renders without a full
      // re-refresh.
      runAdaptiveMacros()
        .then((res) => {
          if (!res) return;
          setAdaptResult(res);
          if (res.applied && supabase) {
            loadGoalsRemote().then(setGoals).catch(() => { /* silent */ });
          }
        })
        .catch(() => { /* silent — adaptation never breaks the dashboard */ });
    } catch (err) {
      // Only reached if at least one load failed AND nothing was cached.
      // Fresh install + no network → we have nothing to show.
      setLoadError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshWater = useCallback(async () => {
    try {
      setWater(await loadTodayWater());
    } catch (err) {
      setLoadError(friendlyError(err));
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    onReviewing(stage.kind === "review");
    if (onStageChange) {
      const mode = (stage as { mode?: string }).mode;
      const mealType = (stage as { mealType?: string }).mealType;
      const parts = [stage.kind, mode, mealType].filter(Boolean).join("/");
      onStageChange(parts);
    }
  }, [stage, onReviewing, onStageChange]);

  useEffect(() => {
    if (justSavedCalories === null) return;
    const t = setTimeout(() => setJustSavedCalories(null), 2500);
    return () => clearTimeout(t);
  }, [justSavedCalories]);

  // "dayMeals" — meals on the currently-selected date (not necessarily today).
  // The variable names below (and the prop `todayMeals` we pass down) preserve
  // the original "today" semantics for the dashboard UI even when we're
  // looking at a different day.
  const dayMeals = mealsOnDate(meals, selectedDate);
  const dayTotals = sumTotals(dayMeals);
  const buckets = mealsByType(dayMeals);

  // Yesterday's totals for day-over-day comparison + "repeat yesterday" chips.
  // Memoized via the simple .toISOString() trick — both are pure functions
  // of `selectedDate` so React will recompute when the date nav changes.
  const yesterdayISO = (() => {
    const d = new Date(`${selectedDate}T00:00:00`);
    d.setDate(d.getDate() - 1);
    return d.toISOString().slice(0, 10);
  })();
  const yesterdayMeals = mealsOnDate(meals, yesterdayISO);
  const yesterdayTotals = sumTotals(yesterdayMeals);
  const yesterdayBuckets = mealsByType(yesterdayMeals);

  /**
   * Compute a `logged_at` timestamp for a new meal. When viewing today, use
   * the current moment (preserves time-of-day for streak math). When viewing
   * any other day, anchor to that day's local noon so the meal lands on the
   * intended calendar day regardless of timezone shenanigans.
   */
  function loggedAtForSelectedDate(): string | undefined {
    if (selectedDate === todayISO()) return undefined; // saveMealRemote defaults to now()
    // `${selectedDate}T12:00:00` is parsed as local time; toISOString() converts to UTC.
    return new Date(`${selectedDate}T12:00:00`).toISOString();
  }

  function startAdd(mealType: MealType) {
    setStage({ kind: "picker", mealType });
  }

  async function quickLogMeal(source: LoggedMeal, quantity: number = 1) {
    if (stage.kind !== "picker") return;
    const targetMealType = stage.mealType;
    try {
      const scaled = scaleEstimate(source.estimate, quantity);
      await saveMealRemote({
        meal_type: targetMealType,
        estimate: scaled,
        caption: source.caption,
        source_providers: source.source_providers,
        disagreement_pct: source.disagreement_pct,
        externalPhotoUrl: source.photo_data_url?.startsWith("http") ? source.photo_data_url : undefined,
        logged_at: loggedAtForSelectedDate()
      });
      await refresh();
      setJustSavedCalories(Math.round(scaled.totals.calories));
      setStage({ kind: "today" });
    } catch (err) {
      setLoadError(friendlyError(err));
    }
  }

  async function quickLogFavorite(f: Favorite, quantity: number = 1) {
    if (stage.kind !== "picker") return;
    const targetMealType = stage.mealType;
    try {
      const scaled = scaleEstimate(f.estimate, quantity);
      await saveMealRemote({
        meal_type: targetMealType,
        estimate: scaled,
        caption: f.caption,
        source_providers: f.source_providers,
        externalPhotoUrl: f.external_photo_url,
        logged_at: loggedAtForSelectedDate()
      });
      await refresh();
      setJustSavedCalories(Math.round(scaled.totals.calories));
      setStage({ kind: "today" });
    } catch (err) {
      setLoadError(friendlyError(err));
    }
  }

  async function quickLogSearchResult(r: SearchResult, quantity: number = 1) {
    if (stage.kind !== "picker") return;
    const targetMealType = stage.mealType;
    try {
      const scaled = scaleEstimate(r.estimate, quantity);
      await saveMealRemote({
        meal_type: targetMealType,
        estimate: scaled,
        source_providers: r.code ? [`off-search:${r.code}`] : ["off-search"],
        externalPhotoUrl: r.imageUrl,
        logged_at: loggedAtForSelectedDate()
      });
      await refresh();
      setJustSavedCalories(Math.round(scaled.totals.calories));
      setStage({ kind: "today" });
    } catch (err) {
      setLoadError(friendlyError(err));
    }
  }

  async function quickLogAiSuggestion(s: AiSuggestion, quantity: number = 1) {
    if (stage.kind !== "picker") return;
    const targetMealType = stage.mealType;
    try {
      const baseEstimate: MealEstimate = {
        items: [
          {
            name: s.name,
            portion: s.portion,
            calories: s.calories,
            protein_g: s.protein_g,
            carbs_g: s.carbs_g,
            fat_g: s.fat_g,
            fiber_g: s.fiber_g
          }
        ],
        totals: {
          calories: s.calories,
          protein_g: s.protein_g,
          carbs_g: s.carbs_g,
          fat_g: s.fat_g,
          fiber_g: s.fiber_g ?? 0
        },
        confidence: "medium",
        clarifying_questions: [],
        notes: s.reason
      };
      const estimate = scaleEstimate(baseEstimate, quantity);
      await saveMealRemote({
        meal_type: targetMealType,
        estimate,
        source_providers: ["gemini-suggest"],
        logged_at: loggedAtForSelectedDate()
      });
      await refresh();
      setJustSavedCalories(Math.round(estimate.totals.calories));
      setStage({ kind: "today" });
    } catch (err) {
      setLoadError(friendlyError(err));
    }
  }

  async function quickLogGeneric(food: GenericFood, quantity: number = 1) {
    if (stage.kind !== "picker") return;
    const targetMealType = stage.mealType;
    try {
      const estimate = scaleEstimate(genericFoodToEstimate(food), quantity);
      await saveMealRemote({
        meal_type: targetMealType,
        estimate,
        source_providers: [`generic:${food.name.toLowerCase()}`],
        logged_at: loggedAtForSelectedDate()
      });
      await refresh();
      setJustSavedCalories(Math.round(estimate.totals.calories));
      setStage({ kind: "today" });
    } catch (err) {
      setLoadError(friendlyError(err));
    }
  }

  function setMode(mode: CaptureMode) {
    if (stage.kind !== "capture") return;
    setStage({ kind: "capture", mode, mealType: stage.mealType });
  }

  async function analyzePhoto({ imageDataUrl, caption }: { imageDataUrl: string; caption: string }) {
    if (stage.kind !== "capture") return;
    const mealType = stage.mealType;
    const ac = newAbortController();
    setStage({ kind: "analyzing", mode: "photo", mealType, imageDataUrl, caption });
    try {
      const { reconciled, perProvider, disagreement_pct } = await estimateMeal(
        { imageDataUrl, caption },
        { signal: ac.signal, onProgress: setAnalyzingPhase }
      );
      const providers = perProvider.filter((p) => "items" in p.result).map((p) => p.name);
      setStage({
        kind: "review",
        mealType,
        imageDataUrl,
        caption,
        estimate: reconciled,
        disagreement_pct,
        sourceProviders: providers
      });
    } catch (err) {
      if ((err as Error)?.name === "AbortError") return; // user canceled — leave stage to cancelAnalyze
      setStage({
        kind: "error",
        mode: "photo",
        mealType,
        message: friendlyError(err),
        imageDataUrl,
        caption
      });
    }
  }

  async function refineEstimate(additionalInfo: string) {
    if (stage.kind !== "review") return;
    const mealType = stage.mealType;
    const imageDataUrl = stage.imageDataUrl;
    const previousCaption = stage.caption;
    const sourceProviders = stage.sourceProviders;

    const newCaption = previousCaption
      ? `${previousCaption}\n\nAdditional info: ${additionalInfo}`
      : additionalInfo;

    // Pick a mode for the analyzing/error transitions — irrelevant for the
    // analyzing render but used by the error stage if anything fails.
    const mode: CaptureMode = imageDataUrl ? "photo" : "describe";
    const ac = newAbortController();
    setStage({ kind: "analyzing", mode, mealType, imageDataUrl, caption: newCaption });
    try {
      const { reconciled } = await estimateMeal(
        { imageDataUrl, caption: newCaption },
        { signal: ac.signal, onProgress: setAnalyzingPhase }
      );
      setStage({
        kind: "review",
        mealType,
        imageDataUrl,
        caption: newCaption,
        estimate: reconciled,
        disagreement_pct: 0,
        sourceProviders
      });
    } catch (err) {
      if ((err as Error)?.name === "AbortError") return;
      setStage({
        kind: "error",
        mode,
        mealType,
        message: friendlyError(err),
        imageDataUrl,
        caption: newCaption
      });
    }
  }

  async function analyzeDescribe(description: string) {
    if (stage.kind !== "capture") return;
    const mealType = stage.mealType;
    const ac = newAbortController();
    setStage({ kind: "analyzing", mode: "describe", mealType, caption: description });
    try {
      const { reconciled } = await estimateMeal(
        { caption: description },
        { signal: ac.signal, onProgress: setAnalyzingPhase }
      );
      setStage({
        kind: "review",
        mealType,
        caption: description,
        estimate: reconciled,
        disagreement_pct: 0,
        sourceProviders: ["gemini-text"]
      });
    } catch (err) {
      if ((err as Error)?.name === "AbortError") return;
      setStage({
        kind: "error",
        mode: "describe",
        mealType,
        message: friendlyError(err),
        caption: description
      });
    }
  }

  function analyzeRecipe({ estimate, name }: { estimate: import("../lib/types").MealEstimate; name: string }) {
    if (stage.kind !== "capture") return;
    const mealType = stage.mealType;
    // Recipe AI already returns a fully-formed per-serving estimate, so we
    // skip the analyzing stage and jump straight to review. Inject the
    // recipe name as the first item's name if no items came back (defensive).
    const finalEstimate = estimate.items.length === 0
      ? { ...estimate, items: [{ name, portion: "1 serving", calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0 }] }
      : estimate;
    setStage({
      kind: "review",
      mealType,
      caption: name, // surfaces "Recipe: name" in any caption-dependent UI
      estimate: finalEstimate,
      disagreement_pct: 0,
      sourceProviders: ["recipe-ai"]
    });
  }

  async function analyzeBarcode({ barcode }: { barcode: string; imageDataUrl?: string }) {
    if (stage.kind !== "capture") return;
    const mealType = stage.mealType;
    // For barcode meals we deliberately drop the user's barcode photo —
    // the OFF product image is what we want to display instead.
    setStage({ kind: "analyzing", mode: "barcode", mealType });
    try {
      // Try Open Food Facts first (better international + crowdsourced
      // coverage). On miss, fall back to USDA FoodData Central (better US
      // branded coverage). On both misses, surface the original OFF error.
      let estimate;
      let imageUrl: string | undefined;
      let source: string;
      try {
        const off = await lookupBarcode(barcode);
        estimate = off.estimate;
        imageUrl = off.imageUrl;
        source = `barcode:${barcode}`;
      } catch (offErr) {
        const usda = await lookupUsdaBarcode(barcode);
        if (usda) {
          estimate = usda.estimate;
          imageUrl = usda.imageUrl;
          source = `barcode-usda:${barcode}`;
        } else {
          throw offErr; // both missed — original OFF error has best user message
        }
      }
      setStage({
        kind: "review",
        mealType,
        externalPhotoUrl: imageUrl,
        estimate,
        disagreement_pct: 0,
        sourceProviders: [source]
      });
    } catch (err) {
      setStage({
        kind: "error",
        mode: "barcode",
        mealType,
        message: friendlyError(err),
        barcode
      });
    }
  }

  async function save(estimate: MealEstimate, mealType: MealType) {
    if (stage.kind !== "review") return;
    setSaving(true);
    setSaveError(null);
    try {
      await saveMealRemote({
        meal_type: mealType,
        estimate,
        caption: stage.caption,
        source_providers: stage.sourceProviders,
        disagreement_pct: stage.disagreement_pct,
        imageDataUrl: stage.imageDataUrl,
        externalPhotoUrl: stage.externalPhotoUrl,
        logged_at: loggedAtForSelectedDate()
      });
      await refresh();
      setJustSavedCalories(Math.round(estimate.totals.calories));
      setStage({ kind: "today" });
    } catch (err) {
      // Stay on the review screen so the user's edits aren't lost. Surface
      // the error IN the review screen via the saveError prop (the top
      // loadError banner only renders inside Today view).
      const msg = friendlyError(err);
      console.error("saveMealRemote failed:", err);
      setSaveError(`${msg}\n\nYour edits are still here — tap Save again.`);
    } finally {
      setSaving(false);
    }
  }

  async function onDelete(id: string) {
    try {
      await deleteMealRemote(id);
      await refresh();
    } catch (err) {
      setLoadError(friendlyError(err));
    }
  }

  function onEdit(id: string) {
    navigate(`/edit/${id}`);
  }

  function onFavorite(meal: LoggedMeal) {
    const names = meal.estimate.items.map((i) => i.name).filter(Boolean);
    const suggested =
      names.length === 0
        ? "Favorite meal"
        : names.length === 1
        ? names[0]
        : `${names[0]} + ${names.length - 1} more`;
    setPendingFavorite({ meal, suggested: suggested.slice(0, 60) });
  }

  async function confirmFavorite(name: string | null) {
    const pending = pendingFavorite;
    setPendingFavorite(null);
    if (!pending || !name) return;
    try {
      await saveFavoriteFromMeal(pending.meal, name);
      await refresh(); // surfaces the new favorite in the picker right away
    } catch (err) {
      setLoadError(friendlyError(err));
    }
  }

  function backToToday() {
    setStage({ kind: "today" });
  }

  return (
    <>
      {justSavedCalories !== null && (
        <div className="fixed top-4 inset-x-0 mx-auto max-w-md px-4 z-50 pointer-events-none">
          <div className="bg-emerald-500 text-slate-950 font-semibold rounded-lg p-3 text-center shadow-lg">
            Saved · {justSavedCalories} kcal
          </div>
        </div>
      )}

      {stage.kind === "analyzing" && (
        <AnalyzingOverlay
          phase={analyzingPhase}
          onCancel={cancelAnalyze}
          mode={stage.mode}
          thumbnailDataUrl={"imageDataUrl" in stage ? stage.imageDataUrl : undefined}
        />
      )}

      {pendingFavorite && (
        <PromptModal
          title="Save as favorite"
          description="Pick a short name — you'll be able to quick-log this in one tap from now on."
          defaultValue={pendingFavorite.suggested}
          placeholder="e.g. Morning oatmeal"
          confirmLabel="Save favorite"
          onClose={confirmFavorite}
          maxLength={80}
        />
      )}

      {stage.kind === "today" && (
        <div className="space-y-4 pb-4">
          <MigrationBanner onDone={refresh} />

          {loading && <DashboardSkeleton />}

          {staleSince !== null && !loading && (
            <div className="mx-4 px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-300 text-xs flex items-center gap-2">
              <WifiOff className="w-3 h-3 text-slate-400" />
              <span>
                Offline — showing data from <span className="font-medium text-slate-200">{formatSyncedAt(staleSince)}</span>.
              </span>
              <button
                type="button"
                onClick={() => refresh()}
                className="ml-auto text-amber-200 underline text-xs"
              >
                Retry
              </button>
            </div>
          )}

          {loadError && (
            <div className="mx-4 p-3 rounded-lg bg-red-950 border border-red-800 text-red-200 text-sm">
              {loadError}
            </div>
          )}

          {!loading && (
            <>
              <DateNav
                selectedDate={selectedDate}
                todayDate={todayISO()}
                onChange={setSelectedDate}
              />

              <div className="pt-2">
                <DailyDashboard
                  totals={dayTotals}
                  goals={goals}
                  mealCount={dayMeals.length}
                  streak={computeStreak(meals)}
                  water={water}
                  onWaterChange={refreshWater}
                />
              </div>

              {/* Day-over-day delta — shown only when both days have at
                  least one meal logged. Free dopamine hit + makes Today
                  feel intelligent vs passive. */}
              {dayMeals.length > 0 && yesterdayMeals.length > 0 && (
                <DayOverDayDelta today={dayTotals} yesterday={yesterdayTotals} />
              )}

              {adaptResult?.applied && adaptResult.old_calories && adaptResult.new_calories && (
                <div className="mx-4 p-3 rounded-2xl bg-slate-900 border border-slate-800 border-l-4 border-l-emerald-400 space-y-1 text-sm">
                  <div className="text-slate-100 font-medium">
                    Adaptive macros: target {adaptResult.new_calories > adaptResult.old_calories ? "↑" : "↓"} to <span className="tabular-nums">{adaptResult.new_calories}</span> kcal
                  </div>
                  <div className="text-xs text-slate-400 leading-relaxed">
                    Based on your weight trend over the last 14 days — was {adaptResult.old_calories}, now {adaptResult.new_calories} ({adaptResult.pct_adjust && adaptResult.pct_adjust > 0 ? "+" : ""}{adaptResult.pct_adjust}%). Disable in Settings if you'd rather stay manual.
                  </div>
                  <button
                    onClick={() => setAdaptResult(null)}
                    className="text-[11px] text-slate-500 hover:text-slate-300"
                  >
                    Dismiss
                  </button>
                </div>
              )}

              {meals.length === 0 && (
                <div className="mx-4 p-4 rounded-2xl bg-slate-900 border border-slate-800 space-y-3">
                  <div>
                    <div className="text-sm text-slate-100 font-semibold mb-1">Welcome 👋</div>
                    <div className="text-xs text-slate-400 leading-relaxed">
                      Snap a photo, scan a barcode, search, or describe your meal — all in one place.
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      // Pick the meal type that matches the current hour so the
                      // first-meal flow doesn't dump them into "Breakfast" at 8 PM.
                      const h = new Date().getHours();
                      const mt: MealType = h < 10 ? "breakfast" : h < 14 ? "lunch" : h < 21 ? "dinner" : "snack";
                      startAdd(mt);
                    }}
                    className="w-full py-2.5 rounded-lg bg-emerald-500 text-slate-950 font-semibold text-sm flex items-center justify-center gap-2"
                  >
                    <Camera className="w-4 h-4" strokeWidth={2.5} />
                    Log your first meal
                  </button>
                </div>
              )}

              <div className="px-4 space-y-3">
                {MEAL_TYPES.map((t) => (
                  <MealSection
                    key={t}
                    type={t}
                    meals={buckets[t]}
                    targetKcal={targetKcalForMealType(goals.calories, t)}
                    yesterdayMeal={yesterdayBuckets[t]?.[0] ?? null}
                    onAdd={startAdd}
                    onDelete={onDelete}
                    onEdit={onEdit}
                    onFavorite={onFavorite}
                    onLogAgain={(m) => quickLogMeal(m, 1)}
                    onShareSection={(sectionMeals, label) => setShareBatch({ meals: sectionMeals, label })}
                  />
                ))}
              </div>

              <WeeklyChart meals={meals} goals={goals} />
              <WeeklyInsight meals={meals} goals={goals} />
            </>
          )}
        </div>
      )}

      {stage.kind === "picker" && (
        <AddFoodPicker
          mealType={stage.mealType}
          meals={meals}
          favorites={favorites}
          onCancel={backToToday}
          onChoosePhoto={() => setStage({ kind: "capture", mode: "photo", mealType: stage.mealType })}
          onChooseBarcode={() => setStage({ kind: "capture", mode: "barcode", mealType: stage.mealType })}
          onChooseDescribe={() => setStage({ kind: "capture", mode: "describe", mealType: stage.mealType })}
          onChooseRecipe={() => setStage({ kind: "capture", mode: "recipe", mealType: stage.mealType })}
          onQuickLogMeal={quickLogMeal}
          onQuickLogFavorite={quickLogFavorite}
          onQuickLogSearchResult={quickLogSearchResult}
          onQuickLogGeneric={quickLogGeneric}
          onQuickLogAiSuggestion={quickLogAiSuggestion}
          onEditAsReview={(estimate, mt, source) => {
            // Quick-log Edit details path — drop the user into MealReview
            // with the pre-scaled estimate. They get the full per-item
            // wheel picker, custom portion, macro editing. Save flow then
            // persists as a brand new meal in their account.
            setStage({
              kind: "review",
              mealType: mt,
              estimate,
              disagreement_pct: 0,
              sourceProviders: [source]
            });
          }}
          goals={goals}
          todayMeals={dayMeals}
        />
      )}

      {(stage.kind === "capture" || stage.kind === "analyzing") && (
        <>
          <div className="px-4 pt-4 flex items-center justify-between">
            <button onClick={backToToday} className="text-sm text-slate-400">
              ← Back
            </button>
            <span className="text-sm text-slate-400">
              Adding to <span className="text-emerald-400">{MEAL_TYPE_LABELS[stage.mealType]}</span>
            </span>
          </div>

          <div className="px-4 pt-3">
            <div className="flex bg-slate-900 border border-slate-800 rounded-lg p-1 overflow-x-auto">
              {(["photo", "barcode", "describe", "recipe"] as const).map((m) => (
                <button
                  key={m}
                  onClick={() => setMode(m)}
                  disabled={stage.kind === "analyzing"}
                  className={`flex-1 py-2 rounded text-sm font-medium whitespace-nowrap ${
                    stage.mode === m ? "bg-slate-800 text-emerald-400" : "text-slate-400"
                  }`}
                >
                  {m === "photo" ? "Photo" : m === "barcode" ? "Barcode" : m === "describe" ? "Describe" : "Recipe"}
                </button>
              ))}
            </div>
          </div>

          {stage.mode === "photo" && (
            <PhotoCapture onSubmit={analyzePhoto} busy={stage.kind === "analyzing"} />
          )}
          {stage.mode === "barcode" && (
            <BarcodeCapture onBarcode={analyzeBarcode} busy={stage.kind === "analyzing"} />
          )}
          {stage.mode === "describe" && (
            <DescribeCapture onSubmit={analyzeDescribe} busy={stage.kind === "analyzing"} />
          )}
          {stage.mode === "recipe" && (
            <RecipeCapture onSubmit={analyzeRecipe} busy={stage.kind === "analyzing"} />
          )}
        </>
      )}

      {stage.kind === "review" && (
        <MealReview
          initial={stage.estimate}
          initialMealType={stage.mealType}
          disagreementPct={stage.disagreement_pct}
          goals={goals}
          alreadyEatenToday={dayTotals}
          saving={saving}
          saveError={saveError}
          onConfirm={save}
          onCancel={backToToday}
          onRefine={refineEstimate}
        />
      )}

      {stage.kind === "error" && (
        <div className="p-4 space-y-3">
          <div className="p-3 rounded-lg bg-red-950 border border-red-800 text-red-200">
            {stage.message}
          </div>

          {/* Show a thumbnail of what they captured so they know it's not lost. */}
          {stage.imageDataUrl && (
            <div>
              <div className="text-xs text-slate-400 mb-1.5">Your photo is preserved:</div>
              <img
                src={stage.imageDataUrl}
                alt="Captured meal"
                className="w-full max-h-40 object-contain rounded-lg bg-slate-900 border border-slate-800"
              />
            </div>
          )}
          {stage.caption && (
            <div>
              <div className="text-xs text-slate-400 mb-1">Your caption:</div>
              <div className="p-2 rounded bg-slate-900 border border-slate-800 text-sm text-slate-200">
                {stage.caption}
              </div>
            </div>
          )}

          <div className="flex gap-2">
            <button
              onClick={backToToday}
              className="flex-1 py-3 rounded-lg border border-slate-700 text-slate-300"
            >
              Back
            </button>
            {/* Retry using the preserved data — no need to re-snap/re-type.
                Picks the right re-runner based on the capture mode. */}
            {(stage.imageDataUrl || stage.caption || stage.barcode) ? (
              <button
                onClick={() => {
                  const { mode, mealType, imageDataUrl, caption, barcode } = stage;
                  // Hop back into "capture" so the analyze* functions accept input
                  setStage({ kind: "capture", mode, mealType });
                  // Schedule the re-analyze on the next tick so the stage update
                  // is committed first (analyzePhoto checks for stage.kind === "capture").
                  setTimeout(() => {
                    if (mode === "photo" && imageDataUrl) {
                      analyzePhoto({ imageDataUrl, caption: caption ?? "" });
                    } else if (mode === "describe" && caption) {
                      analyzeDescribe(caption);
                    } else if (mode === "barcode" && barcode) {
                      analyzeBarcode({ barcode });
                    }
                  }, 0);
                }}
                className="flex-1 py-3 rounded-lg bg-emerald-500 text-slate-950 font-semibold"
              >
                Retry estimate
              </button>
            ) : (
              <button
                onClick={() => setStage({ kind: "capture", mode: stage.mode, mealType: stage.mealType })}
                className="flex-1 py-3 rounded-lg bg-slate-800 text-slate-100"
              >
                Start over
              </button>
            )}
          </div>
        </div>
      )}

      {/* Bulk-share modal — opened by tapping "Share breakfast (N)" on a
          MealSection. Closed by ShareMealModal calling its onClose. */}
      {shareBatch && (
        <ShareMealModal
          meals={shareBatch.meals}
          bulkLabel={shareBatch.label}
          onClose={() => setShareBatch(null)}
        />
      )}
    </>
  );
}

// ----------------------------------------------------------------------------
// Day-over-day delta card — small "vs yesterday" deltas under the dashboard.
// Only renders when there's a meaningful comparison (both days have meals).
// ----------------------------------------------------------------------------

function DayOverDayDelta({
  today,
  yesterday
}: {
  today: { calories: number; protein_g: number; carbs_g: number; fat_g: number };
  yesterday: { calories: number; protein_g: number; carbs_g: number; fat_g: number };
}) {
  const kcalDelta = Math.round(today.calories - yesterday.calories);
  const proteinDelta = Math.round(today.protein_g - yesterday.protein_g);
  return (
    <div className="mx-4 px-3 py-2 rounded-lg bg-slate-900 border border-slate-800 text-[11px] text-slate-400 flex items-center justify-between flex-wrap gap-x-3">
      <span className="text-slate-500 uppercase tracking-wider">vs yesterday</span>
      <span className="tabular-nums">
        <span className={kcalDelta === 0 ? "text-slate-500" : kcalDelta < 0 ? "text-emerald-400" : "text-amber-400"}>
          {kcalDelta > 0 ? "↑" : kcalDelta < 0 ? "↓" : "→"} {Math.abs(kcalDelta)} kcal
        </span>
        <span className="text-slate-600"> · </span>
        <span className={proteinDelta >= 0 ? "text-sky-400" : "text-slate-400"}>
          {proteinDelta > 0 ? "↑" : proteinDelta < 0 ? "↓" : "→"} {Math.abs(proteinDelta)}g protein
        </span>
      </span>
    </div>
  );
}
